import { useEffect, useRef, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { saveCollegeListToBackend } from '../utils/collegeListSync';

// Custom hook for auto-saving data to localStorage and backend
export const useAutoSave = (key, data, delay = 1000) => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const timeoutRef = useRef(null);
  const lastSavedRef = useRef(null);

  useEffect(() => {
    // Save data regardless of authentication status for localStorage persistence
    if (!data) return;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for debounced save
    timeoutRef.current = setTimeout(async () => {
      try {
        // Save to localStorage (always works)
        localStorage.setItem(key, JSON.stringify(data));
        
        // Sync to backend if authenticated and this is college list
        if (isAuthenticated && user && user.sub && key === 'myCollegeList') {
          try {
            const token = await getAccessTokenSilently();
            // Use relative URL - will go through Amplify proxy to backend
            const apiBaseUrl = process.env.REACT_APP_EXPRESS_BACKEND_URL || process.env.REACT_APP_API_BASE_URL || '';
            await saveCollegeListToBackend(data, token, apiBaseUrl);
            console.log(`[useAutoSave] Saved ${key} to backend`);
          } catch (error) {
            console.error(`[useAutoSave] Error saving ${key} to backend:`, error);
            // Don't fail - localStorage save succeeded
          }
        } else {
          console.log(`[useAutoSave] Saved ${key} to localStorage`);
        }
        
      } catch (error) {
        console.error(`[useAutoSave] Error saving ${key}:`, error);
      }
    }, delay);

    // Cleanup timeout on unmount or data change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, isAuthenticated, user, key, delay, getAccessTokenSilently]);

  // Force save immediately (useful for critical operations)
  const forceSave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    try {
      localStorage.setItem(key, JSON.stringify(data));
      
      // Update consolidated user data (only if authenticated)
      if (isAuthenticated && user && user.sub) {
        // Assuming updateUserData is part of the Auth0 context or similar
        // For now, we'll just log the save
        console.log(`[useAutoSave] Force saved ${key} data`);
      }
      
    } catch (error) {
      console.error(`[useAutoSave] Error force saving ${key}:`, error);
    }
  };

  return { forceSave };
};

/**
 * Hook for saving data immediately without debouncing
 * @param {string} key - The localStorage key to save
 * @param {any} data - The data to save
 */
export const useImmediateSave = (key, data) => {
  const { user, isAuthenticated } = useAuth0();

  useEffect(() => {
    // Save data regardless of authentication status for localStorage persistence
    if (!data) return;

    try {
      localStorage.setItem(key, JSON.stringify(data));
      
      // Update consolidated user data (only if authenticated)
      if (isAuthenticated && user && user.sub) {
        // Assuming updateUserData is part of the Auth0 context or similar
        // For now, we'll just log the save
        console.log(`[useImmediateSave] Saved ${key} data immediately`);
      }
      
    } catch (error) {
      console.error(`[useImmediateSave] Error saving ${key}:`, error);
    }
  }, [data, isAuthenticated, user, key]);
}; 